import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ApiClientError } from '@/lib/api';
import { apiErrorStatusFallbackPresets, resolveApiErrorMessage } from '@/lib/api-error-translations';

interface RouteErrorProps {
    error: Error;
}

/**
 * Renders a consistent top-level route error state.
 */
export function RouteError({ error }: RouteErrorProps) {
    const { t } = useTranslation();
    const apiErrorMessage =
        error instanceof ApiClientError
            ? resolveApiErrorMessage(error, t, {
                  fallbackKey: 'apiErrors.organizations.loadFailed',
                  statusFallbacks: apiErrorStatusFallbackPresets.organizationsLoad
              })
            : null;

    return (
        <div className='flex min-h-screen flex-col items-center justify-center gap-6 px-4 text-center'>
            <AlertTriangle className='h-16 w-16 text-destructive' />
            {apiErrorMessage ? (
                <div className='mx-auto max-w-xl rounded-md border border-destructive/30 bg-destructive/5 p-3 text-left text-sm text-destructive'>
                    {apiErrorMessage}
                </div>
            ) : (
                <div className='space-y-2'>
                    <h1 className='text-3xl font-bold'>{t('routeError.heading')}</h1>
                    <p className='text-sm text-muted-foreground'>{t('routeError.description')}</p>
                    <pre className='mx-auto max-w-xl whitespace-pre-wrap rounded-md border bg-muted p-3 text-left text-xs'>
                        {error.message}
                    </pre>
                </div>
            )}
            <Button asChild>
                <Link to='/organizations'>{t('routeError.cta')}</Link>
            </Button>
        </div>
    );
}
